import { useMemo } from 'react';
import { useCurrentUser } from './useCurrentUser';

// Role constants
const VALID_APP_ROLES = ['admin', 'manager', 'gtm-manager', 'gtm', 'agent'];
const IDEAS_VIEW_ROLES = ['admin', 'manager', 'gtm-manager'];
const FULL_MANAGER_ROLES = ['admin', 'manager'];
const SETTINGS_ACCESS_ROLES = ['admin'];
const GTM_ROLES = ['gtm', 'agent'];
const NO_EVIDENCE_POPUP_ROLES = ['gtm-manager', 'gtm', 'agent'];
const BAGEL_OBJECTS_ROLES = ['admin'];

export interface UserPermissions {
  // Role info
  role: string;
  isAdmin: boolean;
  isManager: boolean;
  isGtmManager: boolean;
  isGtm: boolean;
  isAgent: boolean;

  // Idea permissions
  canEditIdeas: boolean;
  canDeleteIdeas: boolean;
  canArchiveIdeas: boolean;
  canEditIdeasInline: boolean;
  canCreateIdeas: boolean;
  
  // Evidence permissions
  canAssignEvidence: boolean;
  canDeclineEvidence: boolean;
  canManageEvidence: boolean;

  // Customer 360 permissions
  canOpenEvidencePopup: boolean;

  // Settings permissions
  canAccessSettings: boolean;

  // Access permissions
  canViewIdeasPage: boolean;
  canAccessApp: boolean;
  shouldRedirectToCustomer360: boolean;

  // Group checks
  isFullManager: boolean;
  isGtmRole: boolean;
}

export const useUserPermissions = (): UserPermissions => {
  const user = useCurrentUser();

  return useMemo(() => {
    const role = user?.role || '';
    const isFullManager = FULL_MANAGER_ROLES.includes(role);

    return {
      // Role info
      role,
      isAdmin: role === 'admin',
      isManager: role === 'manager',
      isGtmManager: role === 'gtm-manager',
      isGtm: role === 'gtm',
      isAgent: role === 'agent',

      // Idea permissions
      canEditIdeas: isFullManager,
      canDeleteIdeas: isFullManager,
      canArchiveIdeas: isFullManager,
      canEditIdeasInline: isFullManager,
      canCreateIdeas: isFullManager,

      // Evidence permissions
      canAssignEvidence: isFullManager,
      canDeclineEvidence: isFullManager,
      canManageEvidence: isFullManager,

      // Customer 360 permissions
      canOpenEvidencePopup: !NO_EVIDENCE_POPUP_ROLES.includes(role),

      // Settings permissions
      canAccessSettings: SETTINGS_ACCESS_ROLES.includes(role),

      // Access permissions
      canViewIdeasPage: IDEAS_VIEW_ROLES.includes(role),
      canAccessApp: VALID_APP_ROLES.includes(role),
      shouldRedirectToCustomer360: GTM_ROLES.includes(role),

      // Group checks
      isFullManager,
      isGtmRole: GTM_ROLES.includes(role),
    };
  }, [user]);
};

// Export constants
export {
  VALID_APP_ROLES,
  IDEAS_VIEW_ROLES,
  FULL_MANAGER_ROLES,
  SETTINGS_ACCESS_ROLES,
  GTM_ROLES,
  NO_EVIDENCE_POPUP_ROLES,
  BAGEL_OBJECTS_ROLES
};
